// eslint-disable-next-line import/no-restricted-paths
import isEqual from "lodash.isequal";
import { useCallback, useEffect, useState } from "react";

import { getStringHTML } from "@/lib/aws-amplify/storage";

import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type * as Type from "../../type";
// eslint-disable-next-line import/no-restricted-paths
import type { FetchResult } from "@apollo/client";

type Props = {
	article:
		| {
				__typename?: "S3Object" | undefined;
				bucket: string;
				key: string;
				region: string;
				mimeType?: string | null | undefined;
				fileName?: string | null | undefined;
		  }
		| null
		| undefined;
	onUpdate: ({ articleHTML }: { articleHTML: string }) => Promise<FetchResult<unknown>>;
	onUpdateSuccessCallback: () => void;
};

/**
 * lang = i18n
 */
export const useCreateMagazineEditorForI18nModel = ({
	article,
	onUpdate: _onUpdate,
	onUpdateSuccessCallback,
}: Props) => {
	const [state, setState] = useState<Type.ViewModel.ForI18nViewModel>({ initialArticleHTML: "", articleHTML: "" });

	const isDirty = !isEqual(state.initialArticleHTML, state.articleHTML);
	const isValid = isDirty;

	useEffect(() => {
		const fetchPlanArticle = async () => {
			const res = await getStringHTML(article);

			setState({ initialArticleHTML: res, articleHTML: res });
		};

		fetchPlanArticle();
	}, [article]);

	const onSetArticleHTML = useCallback((html: string) => {
		setState((prev) => ({ ...prev, articleHTML: html }));
	}, []);

	const resetArticleHTML = useCallback((articleHTML: string) => {
		setState({ initialArticleHTML: articleHTML, articleHTML });
	}, []);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const onUpdate = useCallback(async () => {
		showLoadingScreenCallback();
		const { data, errors } = await _onUpdate({ articleHTML: state.articleHTML }).finally(hideLoadingScreenCallback);

		if (data && !errors) {
			resetArticleHTML(state.articleHTML);
			onUpdateSuccessCallback();
		}
	}, [
		_onUpdate,
		hideLoadingScreenCallback,
		onUpdateSuccessCallback,
		resetArticleHTML,
		showLoadingScreenCallback,
		state.articleHTML,
	]);

	return {
		articleHTML: state.articleHTML,
		onSetArticleHTML,
		isDirty,
		isValid,
		onUpdate,
	};
};
